import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
    HelpCircle, ChevronRight, Shield, Zap, Target, Flag, 
    AlertTriangle, Car, Key, MapPin, Compass, Info,
    Eye, Hand, CheckCircle, Navigation, ArrowRight
} from 'lucide-react';
import styles from './StepCard.module.css';
import ImageViewerModal from '@shared/ui/Modals/ImageViewerModal';

interface StepCardProps {
    number: string;
    title: string;
    description: string;
    icon?: string;
    image?: string;
    isLast?: boolean;
}

const StepCard: React.FC<StepCardProps> = ({
    number,
    title,
    description,
    icon,
    image,
    isLast = false
}) => {
    const [showImageModal, setShowImageModal] = useState(false);

    const getIcon = () => {
        if (!icon || icon.toLowerCase() === 'none') {
            return null;
        }

        switch (icon.toLowerCase().trim()) {
            case 'shield': return <Shield size={20} />;
            case 'zap': return <Zap size={20} />;
            case 'target': return <Target size={20} />;
            case 'flag': return <Flag size={20} />;
            case 'alert':
            case 'warning': return <AlertTriangle size={20} />;
            case 'car': return <Car size={20} />;
            case 'key': return <Key size={20} />;
            case 'map-pin': return <MapPin size={20} />;
            case 'compass': return <Compass size={20} />;
            case 'info': return <Info size={20} />;
            case 'eye': return <Eye size={20} />;
            case 'hand': return <Hand size={20} />;
            case 'check': return <CheckCircle size={20} />;
            case 'nav':
            case 'navigation': return <Navigation size={20} />;
            case 'arrow-right': return <ArrowRight size={20} />;
            default:
                return <ChevronRight size={20} />;
        }
    };

    return (
        <motion.div
            className={styles.stepCardContainer}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
            <div className={styles.timelineColumn}>
                <div className={styles.stepIconContainer}>
                    <span className={styles.stepNumber}>{number}</span>
                </div>
                {!isLast && <div className={styles.timelineConnector} />}
            </div>
            
            <div className={styles.stepContent}>
                <div className={styles.stepHeader}>
                    {getIcon() && (
                        <div className={styles.stepHeaderIcon}>
                            {getIcon()}
                        </div>
                    )}
                    <h3 className={styles.stepTitle}>{title}</h3>
                </div>
                {image && (
                    <div className={styles.stepImageContainer}>
                        <img 
                            src={image} 
                            alt={title} 
                            className={styles.stepImage} 
                            loading="lazy" 
                            onClick={() => setShowImageModal(true)}
                            style={{ cursor: 'pointer' }}
                            role="button"
                            tabIndex={0}
                            aria-label={`View ${title} in full screen`}
                        />
                    </div>
                )}
                <div 
                    className={styles.stepDescription} 
                    dangerouslySetInnerHTML={{ __html: description }} 
                />
            </div>
            
            <ImageViewerModal
                isOpen={showImageModal}
                imageUrl={image || ''}
                imageAlt={title}
                onClose={() => setShowImageModal(false)}
            />
        </motion.div>
    );
};

export default StepCard;
